/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
// import { Mail } from "lucide-react";
// import { teamMembers } from "@/data";
import { JSX } from "react/jsx-runtime";
import { fadeLeft, fadeRight, fadeUp } from "@/lib/animations/variants";
import { values, whyChoose } from "@/lib/constants";

// const LinkedInIcon = () => (
//   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
//     <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
//     <rect x="2" y="9" width="4" height="12" />
//     <circle cx="4" cy="4" r="2" />
//   </svg>
// );

export const Route = createFileRoute("/_public/about")({
  component: AboutPage,
});

function AboutPage(): JSX.Element {
  return (
    <>
      <Helmet>
        <title>About Us | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Learn about Rosewood Tax Consultancy — Kenya's trusted tax professionals delivering compliance, advisory, and dispute resolution across East Africa."
        />
      </Helmet>

      <section className="bg-cream pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className=" text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-6 leading-tight">
              Kenya's Trusted
              <br />
              <span>Tax Partner</span>
            </h1>
            <p className="text-lg text-neutral-800 max-w-xl">
              Whether you need urgent tax help or want to explore how we can
              support your business — we're here.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_380px] gap-16 xl:gap-24 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeLeft}
            >
              <h2 className=" text-4xl md:text-5xl font-semibold text-[#0F0A07] mb-8 leading-tight">
                Built to close a gap the market left open.
              </h2>

              <blockquote className="border-l-4 border-[#C9A84C] pl-6 mb-8">
                <p className=" text-xl text-burgundy italic leading-snug">
                  "Behind every tax return is someone building something — our
                  job is to protect it."
                </p>
              </blockquote>

              <div className="space-y-5 leading-[1.8] text-[1.2rem]">
                <p>
                  Rosewood Consulting is a tax and advisory firm built on a
                  simple belief: every business deserves clear, honest, expert
                  financial guidance — not jargon, confusion, or last-minute
                  panic.
                </p>
                <p>
                  We were founded after watching the same story repeat itself
                  across Kenyan businesses: hardworking entrepreneurs losing
                  money, sleep, and opportunities — not because their businesses
                  were failing, but because the right guidance was never within
                  reach. Records that could be set straight. Penalties that
                  could have been avoided. Assessments that could have been
                  challenged. Structures that could have saved millions.
                </p>
                <p>
                  From start-ups registering their first tax PIN to established
                  companies navigating complex structural issues, we bring the
                  same care, precision, and commitment to every matter. Our team
                  pairs deep knowledge of East Africa's tax law with real-world
                  business experience.
                </p>
                <p>
                  Today, Rosewood Consulting is a trusted partner to clients
                  across industries — turning tax compliance from a burden into
                  a foundation for growth.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeRight}
              className="lg:sticky lg:top-24"
            >
              <div className="bg-burgundy rounded-2xl overflow-hidden">
                <div className="px-8 pt-8 pb-6 border-b border-white/10">
                  <p className="text-gold-light font-semibold tracking-widest uppercase mb-1">
                    Our Mission
                  </p>
                  <p className=" text-white leading-snug mt-3">
                    To provide businesses and individuals with clear, reliable,
                    and practical tax solutions that ensure compliance, minimize
                    risk, and unlock sustainable growth.
                  </p>
                </div>
                <div className="px-8 pt-6 pb-8">
                  <p className="text-gold-light font-semibold tracking-widest uppercase mb-1">
                    Our Vision
                  </p>
                  <p className=" text-white leading-snug mt-3">
                    To be Kenya's most trusted tax and advisory partner — known
                    for integrity, expertise, and the lasting success of our
                    clients.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-14"
          >
            <h2 className="text-4xl md:text-5xl font-semibold leading-tight max-w-xl">
              Five principles. No exceptions.
            </h2>
          </motion.div>

          <div className="divide-y divide-[#D5CCBF]">
            {values.map((value, i) => {
              return (
                <motion.div
                  key={value.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="group grid md:grid-cols-[220px_1fr_40px] gap-6 md:gap-10 items-center py-7 hover:bg-white transition-colors px-4 -mx-4 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <h3 className=" text-xl font-semibold text-[#0F0A07]">
                      {value.title}
                    </h3>
                  </div>
                  <p className="text-lg text-neutral-800 leading-relaxed">
                    {value.description}
                  </p>
                  <div className="hidden md:flex justify-end">
                    <span className=" text-[#D5CCBF] text-2xl font-bold group-hover:text-[#C9A84C] transition-colors">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[400px_1fr] gap-16 xl:gap-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeLeft}
              className="lg:sticky lg:top-24 self-start"
            >
              <h2 className=" text-4xl md:text-5xl font-semibold text-[#0F0A07] leading-tight mb-6">
                What makes us different isn't just what we know.
              </h2>
              <p className="text-lg text-neutral-800 leading-relaxed">
                It's how we work with you — with speed, precision, and a genuine
                investment in your success.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-6">
              {whyChoose.map((item, i) => (
                <motion.div
                  key={item.num}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-cream rounded-2xl p-8 hover:shadow-xs hover:-translate-y-0.5 transition-all"
                >
                  <p className="text-5xl font-bold text-[#E8E0D5] mb-4 leading-none">
                    {item.num}
                  </p>
                  <h3 className="font-semibold text-lg mb-3">
                    {item.title}
                  </h3>
                  <p className="text-neutral-800 leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-14"
          >
            <h2 className=" text-4xl md:text-5xl font-semibold text-white leading-tight max-w-xl">
              The people behind your compliance.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {teamMembers.map((member, i) => (
              <motion.div
                key={member.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="group relative bg-white/5 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all"
              >
                <div className="relative h-44 flex items-center justify-center overflow-hidden">
                  <div
                    aria-hidden
                    className="absolute -top-8 -left-8 w-32 h-32 bg-burgundy rotate-[15deg] opacity-30"
                  />
                  <div className="relative z-10 w-20 h-20 bg-burgundy rounded-full border-2 border-[#C9A84C]/40 flex items-center justify-center shadow-sm">
                    <span className=" text-2xl font-bold text-white">
                      {member.name
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")}
                    </span>
                  </div>
                </div>

                <div className="p-5 pt-0">
                  <div className="h-px bg-gold/20 mb-5" />

                  <h3 className="font-semibold text-white text-base mb-0.5">
                    {member.name}
                  </h3>
                  <p className="text-[#C9A84C] text-xs font-medium mb-3 tracking-wide">
                    {member.role}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {member.qualifications.slice(0, 2).map((q: string) => (
                      <span
                        key={q}
                        className="px-2 py-0.5 bg-white/8 text-white/60 text-[10px] font-medium rounded border border-white/10"
                      >
                        {q}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-white/45 leading-relaxed line-clamp-3 mb-4">
                    {member.bio}
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href={`mailto:${member.email}`}
                      className="w-8 h-8 bg-white/8 rounded-lg flex items-center justify-center hover:bg-burgundy transition-colors group/icon"
                    >
                      <Mail
                        size={12}
                        className="text-white/60 group-hover/icon:text-white"
                      />
                    </a>
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        className="w-8 h-8 bg-white/8 rounded-lg flex items-center justify-center hover:bg-burgundy transition-colors group/icon text-white/60 hover:text-white"
                      >
                        <LinkedInIcon />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}
    </>
  );
}
